import { useRef, type ReactNode } from 'react'
import { InputSwitch } from 'primereact/inputswitch'
import { Menu } from 'primereact/menu'
import type { MenuItem } from 'primereact/menuitem'
import Badge from './Badge'
import Button from './Button'
import EmptyState from './EmptyState'
import SortSpinner from './SortSpinner'

/** Bu paylaşılan kart bileşenini kullanabilmek için satırın taşıması gereken minimum alanlar. */
export interface ReorderableLookupRow {
  id: number
  nameTr: string
  nameEn?: string | null
  recordStatus: number
}

export interface ReorderableLookupLabels {
  active: string
  passive: string
  edit: string
  delete: string
  toggle: string
  moveUp: string
  moveDown: string
  /**
   * Kebab (⋮) butonunun erişilebilir adı. Ayrı bir alan olmak ZORUNDA: menüyü açan buton
   * `edit` ile etiketlenirse ekran okuyucu onu "Düzenle" diye anons eder, oysa buton menüyü
   * açar — düzenleme o menünün yalnız bir maddesidir. Bu bileşen altı ekranda kullanıldığı
   * için hata da altı ekrana yayılıyordu.
   */
  menu: string
}

interface Props<T extends ReorderableLookupRow> {
  items: T[]
  loading: boolean
  /** İşlem süren satır (078-b `busyId` deseni) — o satırın kontrolleri kilitlenir. */
  busyId: number | null
  /** Yeniden sıralama sürüyor mu — tüm oklar kilitlenir. */
  reordering: boolean
  onMove: (row: T, dir: 'up' | 'down') => void
  onEdit: (row: T) => void
  onToggle: (row: T, active: boolean) => void
  onDelete: (row: T) => void
  /**
   * Korumalı/sistem satırı mı (ör. "Diğer" id=-1 — TicketTypes/RejectReasons). Korumalı satırın
   * KENDİ okları ve onu itecek komşu oklar kilitlenir — masaüstü `SortSpinner` ile birebir aynı kural.
   */
  isProtected?: (row: T) => boolean
  /** Kart gövdesine ekstra bir satır (ör. CriteriaScores'un ağırlık %'si). */
  renderExtra?: (row: T) => ReactNode
  emptyIcon?: string
  emptyTitle: string
  emptyDescription: string
  labels: ReorderableLookupLabels
  /**
   * Salt okunur kip (126, 26.10.2026 — Kısıtlı Yetkili asistan): sıralama okları, durum anahtarı ve
   * Düzenle/Sil menüsü çizilmez; durum yine rozetle görünür. Varsayılan `false` — mevcut çağıranlar
   * değişmez.
   */
  readOnly?: boolean
}

/**
 * Düzenle/Sil kebab menüsü — switch ve sıralama okları (ikisi de tekil, sık kullanılan kontroller,
 * ör. mobilde her tıklamada menü açıp kapamak sıralamayı kullanışsız kılardı) dropdown'a girmez;
 * yalnız "tek seferlik" aksiyonlar (Düzenle/Sil) toplanır (v2 madde 1).
 */
function RowMenu<T extends ReorderableLookupRow>({
  row,
  busy,
  onEdit,
  onDelete,
  labels,
}: Readonly<{
  row: T
  busy: boolean
  onEdit: (row: T) => void
  onDelete: (row: T) => void
  labels: ReorderableLookupLabels
}>) {
  const menuRef = useRef<Menu>(null)
  const items: MenuItem[] = [
    { label: labels.edit, icon: 'pi pi-pencil', command: () => onEdit(row) },
    {
      label: labels.delete,
      icon: 'pi pi-trash',
      className: 'ui-card-menu-item--danger',
      command: () => onDelete(row),
    },
  ]
  return (
    <>
      <Menu model={items} popup ref={menuRef} appendTo={document.body} />
      <Button
        icon="pi pi-ellipsis-v"
        rounded
        text
        disabled={busy}
        /* Erişilebilir ad KAYDI da söyler: liste yinelenen kartlardan oluştuğu için yalnız
           "işlemler" diyen altı/dokuz düğme ekran okuyucuda birbirinden ayırt edilemiyordu
           (THY AI review bulgusu, 26.10.2026). Görünen başlık `row.nameTr` olduğu için
           erişilebilir ad da onu içermeli — adın görünen etiketi kapsaması bir kuraldır. */
        aria-label={`${row.nameTr}, ${labels.menu}`}
        onClick={(e) => menuRef.current?.toggle(e)}
      />
    </>
  )
}

/**
 * Paylaşılan mobil/tablet kart görünümü (Reuse Mandate) — "sıralanabilir basit kod tablosu" kalıbını
 * (sıra okları + Ad TR/EN + durum + düzenle/sil) kullanan TÜM ekranlar (Paydaşlar, Etkinlik Türü, Davet
 * Türü, Bilet Talep Türü, Bilet Ret Gerekçesi, Değerlendirme Kriterleri) bunu paylaşır — altı ayrı sayfada
 * altı kez aynı kart JSX'ini yazmak yerine (kullanıcı isteği 2026-08-20: "Admin, Etkinlik Takip ve Bilet
 * Takip için de aynı iyileştirmeleri yapalım"). DOM/veri/mutasyonlar sayfada kalır; bu yalnız SUNUM.
 * `≤1024px`'te (bkz. `CARD_VIEW_BREAKPOINT`) çağıran sayfa bunu `<DataTable>` yerine render eder.
 */
export default function ReorderableLookupCards<T extends ReorderableLookupRow>({
  items,
  loading,
  busyId,
  reordering,
  onMove,
  onEdit,
  onToggle,
  onDelete,
  isProtected,
  renderExtra,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  labels,
  readOnly = false,
}: Readonly<Props<T>>) {
  return (
    <div className="ui-card-list">
      {loading &&
        Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
        ))}
      {!loading && items.length === 0 && (
        <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDescription} />
      )}
      {!loading &&
        items.map((row, rowIndex) => {
          const active = row.recordStatus === 1
          const protectedRow = isProtected?.(row) ?? false
          const disableUp =
            rowIndex === 0 || protectedRow || (isProtected?.(items[rowIndex - 1]) ?? false)
          const disableDown =
            rowIndex === items.length - 1 ||
            protectedRow ||
            (isProtected?.(items[rowIndex + 1]) ?? false)
          return (
            <div key={row.id} className="ui-card">
              <div className="ui-card-top">
                <Badge tone={active ? 'green' : 'neutral'}>
                  {active ? labels.active : labels.passive}
                </Badge>
                {!readOnly && (
                  <div className="ui-card-actions ui-card-actions--compact">
                    <SortSpinner
                      onUp={() => onMove(row, 'up')}
                      onDown={() => onMove(row, 'down')}
                      disableUp={disableUp}
                      disableDown={disableDown}
                      busy={reordering}
                      /* Aynı sebep: "yukarı taşı" tek başına hangi kartı taşıyacağını söylemiyordu. */
                      labelUp={`${row.nameTr}, ${labels.moveUp}`}
                      labelDown={`${row.nameTr}, ${labels.moveDown}`}
                    />
                    <InputSwitch
                      className="admin-switch"
                      checked={active}
                      disabled={busyId === row.id || protectedRow}
                      onChange={(e) => onToggle(row, Boolean(e.value))}
                      aria-label={row.nameTr}
                      tooltip={labels.toggle}
                      tooltipOptions={{ position: 'bottom' }}
                    />
                    <RowMenu
                      row={row}
                      busy={busyId === row.id || protectedRow}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      labels={labels}
                    />
                  </div>
                )}
              </div>
              <div className="ui-card-head">
                <span className="ui-card-title">{row.nameTr}</span>
              </div>
              {row.nameEn && <span className="ui-card-sub">{row.nameEn}</span>}
              {renderExtra?.(row)}
            </div>
          )
        })}
    </div>
  )
}
